import type { Accessor } from "solid-js";
import { keepPreviousData, useQuery } from "@tanstack/solid-query";
import axios from "axios";
import { isEmpty } from "lodash-es";
import { COUNTRIES_BASE_URL } from "../constants.ts";
import type { LanguageCode } from "../state/i18n.tsx";

export type Country = {
  alpha2Code: string;
  name: string;
  callingCode: string;
  translations: Partial<Record<LanguageCode, string>>;
};

type CountriesResponse = {
  current?: Country;
  countries: Country[];
};

export type Countries = {
  current?: Country;
  countries: Country[];
};

type RegionalBlocDto = {
  acronym: string;
  name: string;
};

export type CountryDetailsDto = {
  alpha2Code: string;
  regionalBlocs?: RegionalBlocDto[];
};

const CountriesService = {
  getCountries: () =>
    useQuery<Countries>(() => ({
      queryKey: ["countries", "v2"],
      queryFn: async () => {
        const { data } = await axios.get<CountriesResponse>(COUNTRIES_BASE_URL + "/v2");
        return { current: data.current, countries: data.countries };
      },
      staleTime: Infinity,
    })),
  getCountry: (alpha2Code: Accessor<string | undefined>) =>
    useQuery<CountryDetailsDto>(() => ({
      queryKey: ["country", alpha2Code()],
      queryFn: async () => {
        const { data } = await axios.get<CountryDetailsDto>(`${COUNTRIES_BASE_URL}/${alpha2Code()}`);
        return data;
      },
      enabled: !isEmpty(alpha2Code()),
      // Keep the previous country while the next one loads, so its details don't flicker away
      placeholderData: keepPreviousData,
      staleTime: Infinity,
    })),
};

export default CountriesService;
